/**
 * Returns a circular replacer function for JSON.stringify that removes circular references and excludes the 'ref' property.
 * This is useful when trying to output the 'errors' object returned from the react-hook-form formState
 * The issue is specifically with the 'ref' property which points to the input element and causes a circular reference.
 * This can potentially be removed if the bug opened by Brian Battenfeld is resolved
 * https://github.com/react-hook-form/resolvers/issues/660
 * @returns The circular replacer function.
 */
export const getCircularReplacer = (): ((
  key: string,
  value: unknown
) => unknown) => {
  const seen = new WeakSet();
  return (key: string, value: unknown): unknown => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value) || key === 'ref') {
        return;
      }
      seen.add(value);
    }
    return value;
  };
};
// Example usage: JSON.stringify(cleanCircularReferences(formState.errors, getCircularReplacer()), 2)

export const cleanCircularReferences = (obj: any): unknown => {
  const seen = new WeakSet();
  if (typeof obj === 'object' && obj !== null) {
    if (seen.has(obj)) {
      return;
    }
    seen.add(obj);
    const cleanObj: any = Array.isArray(obj) ? [] : {};
    for (const key in obj) {
      if (key === 'ref') {
        continue;
      }
      cleanObj[key] = cleanCircularReferences(obj[key]);
    }
    return cleanObj;
  }
  return obj;
};
// Example usage: JSON.stringify(cleanCircularReferences(formState.errors))
